import type {
  GovernanceDocumentType,
  DocumentLifecycleStatus,
} from "./api/contentRegistryApi";
import { DEFAULT_TABLE_PAGE_SIZE } from "../../../utils/formatters";

/* ── Constants ── */
export const DOCUMENT_TYPES: { value: GovernanceDocumentType; label: string }[] =
  [
    { value: "POLICY", label: "Policy" },
    { value: "STANDARD", label: "Standard" },
    { value: "GUIDELINE", label: "Guideline" },
    { value: "PROCEDURE", label: "Procedure" },
  ];
export const ReviewCycle_Options: { value: string; label: string }[] =

  [
    { value: "none", label: "No review required" },
    { value: "90", label: "Quarterly" },
    { value: "180", label: "Half-Yearly" },
    { value: "365", label: "Annually" },
    { value: "custom", label: "Custom" },

  ];

export const DOC_TYPE_COLORS: Record<string, string> = {
  POLICY:
    "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200/60 dark:ring-blue-500/20",
  STANDARD:
    "bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 ring-1 ring-teal-200/60 dark:ring-teal-500/20",
  GUIDELINE:
    "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 ring-1 ring-orange-200/60 dark:ring-orange-500/20",
  PROCEDURE:
    "bg-fuchsia-50 dark:bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300 ring-1 ring-fuchsia-200/60 dark:ring-fuchsia-500/20",
};

/* The doc-engine's default registry domain. Used by the ingest (upload/save)
 * calls; domain-scoped list requests instead send the opened domain's code
 * (SCRG, CHG, AST, …), which is the same three-letter code parsed from the
 * icon filenames (see Domain/utils/assets.ts). */
export const DOC_ENGINE_DOMAIN_NAME = "SCRG";

/* The governance domain's single code is `SCRG` — it is both the doc-engine
 * domain name (`DOC_ENGINE_DOMAIN_NAME`) and the code parsed from the icon
 * filename "…Resilience Governance (SCRG).png", which is what
 * `SCF_DOMAINS[].code` and every lookup use. */
const GOVERNANCE_DOMAIN_DESCRIPTION =
  "Govern the organization's Security, Compliance & Resilience Program (SCRCP) through accountable oversight, evidence-based decision-making and demonstrable evidence that the organization is secure, compliant and resilient.";

/* ── SCF domain info ──
 * Official domain descriptions shown under the domain name on the domain
 * documents page. Keyed by the three-letter code parsed from the icon
 * filenames (see Domain/utils/assets.ts). */
export const SCF_DOMAIN_DESCRIPTIONS: Record<string, string> = {
  SCRG: GOVERNANCE_DOMAIN_DESCRIPTION,
  AAT: "Govern Artificial Intelligence & Autonomous Technologies (AAAT) through trustworthy, secure and resilient lifecycle practices that manage intended and unintended outcomes.",
  AST: "Manage Technology Assets, Applications and Services (TAASD) throughout their lifecycle to maintain visibility, traceability, authorization and protection.",
  BCD: "Maintain resilient capabilities to sustain business-critical functions and restore Technology Assets, Applications and Services (TAASD) as documented, tested and maintained continuity and recovery plans.",
  CAP: "Plan current and future capacity and performance requirements for Technology Assets, Applications and Services (TAASD) to sustain reliable operations.",
  CHG: "Manage changes to Technology Assets, Applications and Services (TAASD) through an authorized, risk-based process that approves, implements and documents changes before and after deployment.",
  CLD: "Govern cloud services and environments through risk-based, cloud-agnostic security and resilience practices aligned with shared responsibility obligations.",
  CPL: "Govern security, compliance and data protection obligations to maintain defensible evidence of conformity with applicable internal and external requirements.",
  CFG: "Establish and maintain secure configuration baselines for Technology Assets, Applications and Services (TAASD) that implement least privilege and least functionality to support a defensible secure configuration posture.",
  MON: "Sustain security and resilience through centralized collection, correlation and analysis of security-relevant telemetry from Technology Assets, Applications and Services (TAASD).",
  CRY: "Use appropriate cryptographic mechanisms and industry-recognized key management practices to protect sensitive and regulated data at rest and in transit.",
  DCH: "Enforce a standardized data classification methodology to determine data sensitivity and apply protection controls for Technology Assets, Applications and Services (TAASD) across their data handling, protection, retention and disposal lifecycle.",
  EMB: "Apply risk-based, security, compliance and resilience practices to embedded Technology where compromise or misuse could create operational disruption or data protection impacts.",
  END: "Harden and centrally manage endpoint devices to protect Technology Assets, Applications and Services (TAASD) from unauthorized access, compromise and disruption.",
  HRS: "Establish and enforce personnel security management practices that address screening, onboarding, acceptable behavior, role-based risk, competence and offboarding requirements.",
  IAC: "Implement secure, compliant and resilient Identity and Access Management (IAM) capabilities that enforce least access across human users, devices, service accounts and other Non-Person Entities (NPEs).",
  IRO: "Maintain a compliant incident response capability that enables trained responders to identify, analyze, contain, eradicate and recover from incidents according to documented Incident Response Plans (IRPs).",
  IAO: "Execute Information Assurance (IA) practices to validate that expected security, compliance and resilience controls are appropriately designed and operating as intended for Technology Assets, Applications and Services (TAASD).",
  MNT: "Proactively maintain Technology Assets, Applications and Services (TAASD) through authorized maintenance practices that preserve performance, security, compliance and supportability.",
  MDM: "Govern mobile device access to Technology Assets, Applications and Services (TAASD) to reduce attack surface and data exposure.",
  NET: "Implement compliant and resilient defense-in-depth network protections that segment, restrict and monitor access to Technology Assets, Applications and Services (TAASD).",
  PES: "Protect physical environments through layered security and resilience controls that safeguard Technology Assets, Applications and digital assets from unauthorized access, theft, damage and disruption.",
  PRI: "Execute risk-based and legally defensible data privacy practices that conform with applicable statutory, regulatory and contractual obligations to protect sensitive Personal Data (PD) throughout its lifecycle.",
  PRM: "Operationalize security, compliance and resilience objectives by integrating cybersecurity and data privacy requirements into project, program and resource management activities.",
  RSK: "Proactively identify, assess, prioritize and treat risks to align Technology Assets, Applications and Services (TAASD)-related decisions with the organization's defined risk appetite and risk tolerance.",
  QTS: "Mitigate quantum-enabled cryptographic threats through governance that prepares compliant Post-Quantum Cryptography (PQC) risk management practices.",
  SEA: "Apply industry-recognized secure engineering and architecture principles to deliver secure, compliant and resilient systems, applications and services.",
  OPS: "Implement compliant and resilient security operations through defined processes, skilled personnel, monitoring, escalation and continuous improvement that effectively detect, isolate and remediate cyber threats while ensuring business resilience.",
  SAT: "Build a compliant and resilient security-minded workforce through ongoing, role-based education on evolving threats, obligations and secure workplace practices.",
  TDA: "Develop and acquire Technology Assets, Applications and Services (TAASD) through secure-by-design, risk-informed and resilient lifecycle practices.",
  TPM: "Execute Supply Chain Risk Management (SCRM) practices to assess, select, contract, monitor and manage trustworthy third parties for product and service delivery.",
  THR: "Proactively identify, assess and manage threats to Technology Assets, Applications and Services (TAASD) and business processes to inform risk decisions and corrective actions.",
  VPM: "Reduce exploitable weaknesses in Technology Assets, Applications and Services (TAASD) through coordinated vulnerability identification, prioritization, remediation and patch management.",
  WEB: "Secure web-facing Technology Assets, Applications and Services (TAASD) by minimizing attack surface and monitoring for anomalous activity.",
};

export const LIFECYCLE_STATUSES: {
  value: DocumentLifecycleStatus;
  label: string;
}[] = [
    { value: "DRAFT", label: "Draft" },
    { value: "IN_REVIEW", label: "In Review" },
    { value: "APPROVED", label: "Approved" },
    { value: "PUBLISHED", label: "Published" },
    { value: "RETIRED", label: "Withdrawn" },
  ];

export const TITLE_MAX_LENGTH = 150;
export const EVIDENCE_TITLE_MAX = 100;
export const DAYS_MAX = 366;
/** Max upload size (25 MB) - keep in sync with the "max 25 MB" hint text. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

/* ── Processing-job phases ── */
export const JOB_PHASE = {
  UPLOADING: "uploading",
  RUNNING: "running",
  VALIDATING_LAYERS: "validating-layers",
  COMPLETE: "complete",
  ERROR: "error",
} as const;
export type JobPhase = (typeof JOB_PHASE)[keyof typeof JOB_PHASE];

export const IN_FLIGHT_PHASES: readonly JobPhase[] = [
  JOB_PHASE.UPLOADING,
  JOB_PHASE.RUNNING,
  JOB_PHASE.VALIDATING_LAYERS,
];

/* ── Per-layer classification run status ── */
export const LAYER_RUN_STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  COMPLETE: "complete",
  ERROR: "error",
} as const;
export type LayerRunStatus =
  (typeof LAYER_RUN_STATUS)[keyof typeof LAYER_RUN_STATUS];

/* ── Seven-layer row rendering states ── */
export const LAYER_ROW_STATE = {
  RUNNING: "running",
  COMPLETE: "complete",
  FAILED: "failed",
  NOT_RUN: "not_run",
  PENDING: "pending",
  NOT_EVALUATED: "not_evaluated",
  CLEARED: "cleared",
  NOT_CLEARED: "not_cleared",
} as const;
export type LayerRowState =
  (typeof LAYER_ROW_STATE)[keyof typeof LAYER_ROW_STATE];

export interface LayerRowMeta {
  chipTone: string;
  pillTone: string;
  pillLabel: string;
}

export const LAYER_ROW_META: Record<LayerRowState, LayerRowMeta> = {
  running: {
    chipTone:
      "border-indigo-300 bg-indigo-50 text-indigo-600 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-400",
    pillTone:
      "border-indigo-200 text-indigo-600 dark:border-indigo-500/30 dark:text-indigo-400",
    pillLabel: "Running",
  },
  complete: {
    chipTone:
      "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
    pillTone:
      "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400",
    pillLabel: "Learn more",
  },
  failed: {
    chipTone:
      "border-red-200 bg-red-50 text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400",
    pillTone:
      "border-red-200 text-red-600 dark:border-red-500/30 dark:text-red-400",
    pillLabel: "Failed",
  },
  not_run: {
    chipTone:
      "border-red-200/70 bg-red-50/60 text-red-500 dark:border-red-500/25 dark:bg-red-500/[0.06] dark:text-red-400/70",
    pillTone:
      "border-red-200/70 text-red-500/80 dark:border-red-500/25 dark:text-red-400/70",
    pillLabel: "Not run",
  },
  pending: {
    chipTone:
      "border-border bg-[#f8fafc] text-[#94a3b8] dark:bg-white/[0.02] dark:text-white/30",
    pillTone: "border-border text-muted-foreground",
    pillLabel: "Pending",
  },
  not_evaluated: {
    chipTone:
      "border-slate-200 bg-slate-50 text-slate-500 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white/40",
    pillTone:
      "border-slate-200 text-slate-500 dark:border-white/[0.08] dark:text-white/50",
    pillLabel: "Not evaluated",
  },
  cleared: {
    chipTone:
      "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
    pillTone:
      "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400",
    pillLabel: "Cleared",
  },
  not_cleared: {
    chipTone:
      "border-red-200 bg-red-50 text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400",
    pillTone:
      "border-red-200 text-red-600 dark:border-red-500/30 dark:text-red-400",
    pillLabel: "Not cleared",
  },
};

/* ── Domain-classify drawer ── */
export const CONFIDENCE_STRONG_THRESHOLD = 0.8;
export const CONFIDENCE_MODERATE_THRESHOLD = 0.5;
export const DOMAIN_TABLE_PAGE_SIZE = DEFAULT_TABLE_PAGE_SIZE;
export const CONFIDENCE_RING_SIZE = 180;
export const CONFIDENCE_RING_STROKE = 14;

/* ── Concept drawer ── */
export const CONCEPT_ALL_SECTIONS = "__all__";
export const CONCEPT_SOURCE_GLOSSARY = "glossary_table";
export const CONCEPT_SOURCE_RECURRING = "recurring_term";
export const CONCEPT_DEFINED_SOURCE_HINTS = ["gloss", "defined"] as const;
export const CONCEPT_RECURRING_SOURCE_HINTS = ["recurring", "referenc"] as const;

export const CONCEPT_SOURCE_LABELS: Record<string, string> = {
  [CONCEPT_SOURCE_GLOSSARY]: "Glossary table",
  [CONCEPT_SOURCE_RECURRING]: "Recurring term",
};

export const CONCEPT_FLAG_LABELS: Record<string, string> = {
  not_enabled: "Not enabled",
  enabled: "Enabled",
};

export const CONCEPT_CONF_TONES = {
  none: {
    text: "text-muted-foreground",
    bar: "bg-muted-foreground/40",
    ring: "stroke-muted-foreground/40",
  },
  strong: {
    text: "text-emerald-600 dark:text-emerald-400",
    bar: "bg-gradient-to-r from-emerald-500 to-cyan-500",
    ring: "stroke-emerald-500 dark:stroke-emerald-400",
  },
  moderate: {
    text: "text-amber-600 dark:text-amber-400",
    bar: "bg-gradient-to-r from-amber-500 to-amber-400",
    ring: "stroke-amber-500 dark:stroke-amber-400",
  },
  weak: {
    text: "text-red-600 dark:text-red-400",
    bar: "bg-gradient-to-r from-red-500 to-red-400",
    ring: "stroke-red-500 dark:stroke-red-400",
  },
} as const;

export interface StatusPillMeta {
  label: string;
  cls: string;
  dot: string;
}

export const CONCEPT_STATUS_PILL: Record<string, StatusPillMeta> = {
  complete: {
    label: "Complete",
    cls: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25",
    dot: "bg-emerald-500 dark:bg-emerald-400",
  },
  partial: {
    label: "Partial run",
    cls: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/25",
    dot: "bg-amber-500 dark:bg-amber-400",
  },
  failed: {
    label: "Failed",
    cls: "bg-red-100 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/25",
    dot: "bg-red-500 dark:bg-red-400",
  },
};

export const CONCEPT_STATUS_UNKNOWN_PILL: StatusPillMeta = {
  label: "Unknown",
  cls: "bg-muted text-muted-foreground border-border",
  dot: "bg-muted-foreground",
};

export const CONCEPT_FOOTER_STATUS_LABEL: Record<string, string> = {
  complete: "Complete",
  partial: "Partial",
  failed: "Failed",
};

export type InsightTileTone =
  | "cyan"
  | "indigo"
  | "emerald"
  | "amber"
  | "violet"
  | "slate";

export const INSIGHT_TILE_TONES: Record<
  InsightTileTone,
  { icon: string; value: string }
> = {
  cyan: {
    icon: "text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
    value: "text-cyan-700 dark:text-cyan-300",
  },
  indigo: {
    icon: "text-indigo-600 dark:text-indigo-400 bg-indigo-500/10 border-indigo-500/20",
    value: "text-indigo-700 dark:text-indigo-300",
  },
  emerald: {
    icon: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    value: "text-emerald-700 dark:text-emerald-300",
  },
  amber: {
    icon: "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20",
    value: "text-amber-700 dark:text-amber-300",
  },
  violet: {
    icon: "text-violet-600 dark:text-violet-400 bg-violet-500/10 border-violet-500/20",
    value: "text-violet-700 dark:text-violet-300",
  },
  slate: {
    icon: "text-slate-600 dark:text-slate-400 bg-slate-500/10 border-slate-500/20",
    value: "text-slate-700 dark:text-slate-300",
  },
};

export const CONCEPT_BANNERS = {
  failed: {
    title: "Concept classification failed",
    body: "The extractor could not complete this layer. Any partial results below may be incomplete or missing entirely.",
    cls: "border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
  },
  partial: {
    title: "Partial concept run",
    body: "Not every expected sub-step finished. Results below reflect what was successfully extracted.",
    cls: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  },
} as const;

/* ── Requirement drawer ── */
/** Rows per page in the Requirement list. */
export const REQUIREMENT_PAGE_SIZE = 10;
/** Threshold above which the takeaway calls the doc "almost entirely mandatory". */
export const MANDATORY_SHARE_THRESHOLD = 0.9;

export const REQUIREMENT_DONUT_SIZE = 180;
export const REQUIREMENT_DONUT_STROKE = 22;

export type RequirementStrengthKind =
  | "mandatory"
  | "prohibited"
  | "permitted"
  | "other";

export interface RequirementModalMeta {
  label: string;
  kind: RequirementStrengthKind;
  dot: string;
  stroke: string;
}

export const REQUIREMENT_MODAL_META: Record<string, RequirementModalMeta> = {
  shall: {
    label: "Mandatory (shall)",
    kind: "mandatory",
    dot: "bg-emerald-500 dark:bg-emerald-400",
    stroke: "#10b981",
  },
  must: {
    label: "Mandatory (must)",
    kind: "mandatory",
    dot: "bg-emerald-500 dark:bg-emerald-400",
    stroke: "#10b981",
  },
  "shall not": {
    label: "Prohibited (shall not)",
    kind: "prohibited",
    dot: "bg-red-500 dark:bg-red-400",
    stroke: "#ef4444",
  },
  "must not": {
    label: "Prohibited (must not)",
    kind: "prohibited",
    dot: "bg-red-500 dark:bg-red-400",
    stroke: "#ef4444",
  },
  may: {
    label: "Permitted (may)",
    kind: "permitted",
    dot: "bg-amber-500 dark:bg-amber-400",
    stroke: "#f59e0b",
  },
  should: {
    label: "Recommended (should)",
    kind: "other",
    dot: "bg-indigo-500 dark:bg-indigo-400",
    stroke: "#6366f1",
  },
};

export const REQUIREMENT_OTHER_STROKES = [
  "#818cf8",
  "#a78bfa",
  "#f472b6",
  "#94a3b8",
] as const;

export const REQUIREMENT_STRENGTH_PILL_CLS: Record<RequirementStrengthKind, string> = {
  mandatory:
    "bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25",
  prohibited:
    "bg-red-100 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/25",
  permitted:
    "bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/25",
  other: "bg-muted text-muted-foreground border border-border",
};

export type RequirementStatusKind = "complete" | "partial" | "failed";

export const REQUIREMENT_STATUS_LABEL: Record<RequirementStatusKind, string> = {
  complete: "Complete",
  partial: "Partial",
  failed: "Failed",
};

export const REQUIREMENT_STATUS_PILL_CLS: Record<RequirementStatusKind, string> = {
  complete:
    "bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25",
  partial:
    "bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/25",
  failed:
    "bg-red-100 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/25",
};

export const REQUIREMENT_STATUS_DOT_CLS: Record<RequirementStatusKind, string> = {
  complete: "bg-emerald-500 dark:bg-emerald-400",
  partial: "bg-amber-500 dark:bg-amber-400",
  failed: "bg-red-500 dark:bg-red-400",
};

export const REQUIREMENT_DECOMPOSED_STRONG_CLS =
  "text-emerald-600 dark:text-emerald-400";
export const REQUIREMENT_DECOMPOSED_MODERATE_CLS =
  "text-amber-600 dark:text-amber-400";
export const REQUIREMENT_DECOMPOSED_WEAK_CLS = "text-red-600 dark:text-red-400";

export type RequirementTabKey = "all" | "mandatory" | "prohibited" | "flagged";

/* ── Ingest pipeline safety cap ── */
export const INGEST_STREAM_TIMEOUT_MS = 10 * 60 * 1000;

/* ── Doc-engine ingest save status ── */
export const DOC_INGEST_STATUS = {
  APPROVED: "approved",
  DRAFT: "draft",
} as const;
export type DocIngestStatus =
  (typeof DOC_INGEST_STATUS)[keyof typeof DOC_INGEST_STATUS];

/* ── Document-type badge colours ── */
export const docTypeBadgeStyles: Record<string, string> = {
  POLICY:
    "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:ring-1 dark:ring-blue-500/30",
  STANDARD:
    "bg-slate-100 text-slate-700 ring-1 ring-slate-300 dark:bg-slate-500/20 dark:text-slate-300 dark:ring-1 dark:ring-slate-500/30",
  GUIDELINE:
    "bg-teal-50 text-teal-700 ring-1 ring-teal-200 dark:bg-teal-500/20 dark:text-teal-300 dark:ring-1 dark:ring-teal-500/30",
  PROCEDURE:
    "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-1 dark:ring-amber-500/30",
};

export const defaultDocTypeBadge =
  "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-500/20 dark:text-slate-300 dark:ring-1 dark:ring-slate-500/30";

/* ── Domain Intelligence: placeholders and tones ── */
export const EMPTY_PLACEHOLDER = "-";
export const UNCOVERED_DOMAIN_CODE = "UNCOVERED";
export const NOT_COVERED_STATUS = "NOT_COVERED";

export type StrengthLabel = "Strong" | "Moderate" | "Weak" | typeof EMPTY_PLACEHOLDER;

export const CONF_TONES = {
  none: {
    ring: "stroke-muted-foreground/40",
    text: "text-muted-foreground",
    bar: "bg-muted-foreground/40",
    pillBg: "bg-muted",
    pillText: "text-muted-foreground",
  },
  strong: {
    ring: "stroke-emerald-400",
    text: "text-emerald-400",
    bar: "bg-gradient-to-r from-emerald-500 to-cyan-400",
    pillBg: "bg-emerald-500/15",
    pillText: "text-emerald-400",
  },
  moderate: {
    ring: "stroke-amber-400",
    text: "text-amber-400",
    bar: "bg-gradient-to-r from-amber-500 to-amber-300",
    pillBg: "bg-amber-500/15",
    pillText: "text-amber-400",
  },
  weak: {
    ring: "stroke-red-400",
    text: "text-red-400",
    bar: "bg-gradient-to-r from-red-500 to-red-400",
    pillBg: "bg-red-500/15",
    pillText: "text-red-400",
  },
} as const;

export const STAT_TILE_TONE = {
  cyan: "text-cyan-400",
  indigo: "text-indigo-400",
  emerald: "text-emerald-400",
  amber: "text-amber-400",
} as const;

/* ── Pipeline yield tile tone classes ── */
export type ConsoleYieldTone = "neutral" | "pass" | "warn" | "fail";
export const YIELD_TONE_CLASS: Record<ConsoleYieldTone, string> = {
  neutral: "text-indigo-600 dark:text-indigo-400",
  pass: "text-emerald-600 dark:text-emerald-400",
  warn: "text-amber-600 dark:text-amber-400",
  fail: "text-red-600 dark:text-red-400",
};

export type ConsoleTone = "neutral" | "pass" | "fail";

/* ── Backend stage-name aliases mapped to UI stage keys ──
 *
 * Modern backend stage names (canonical_record, semantic_chunking,
 * chunk_selection, classify, decompose, compare) match UI keys directly and
 * do not need an alias. Only legacy / synonym names live here. */
export const STAGE_KEY_ALIASES: Record<string, string> = {
  parse: "ingestion",
  ocr: "ingestion",
  ocr_parse: "ingestion",
  ingest: "ingestion",
  extract: "canonical_record",
  intelligence_extract: "canonical_record",
  intelligence: "canonical_record",
  chunk: "semantic_chunking",
  semantic: "chunk_selection",
  publish: "hub",
};

/* ── Extraction feed category ordering ── */
export const CATEGORY_ORDER = ["metadata", "structural", "semantic"] as const;

/* ── Governance activity timeline event dot backgrounds ── */
export const EVENT_DOT_BG: Record<string, string> = {
  CREATED: "bg-emerald-100 dark:bg-emerald-500/20",
  SUBMITTED: "bg-blue-100 dark:bg-blue-500/20",
  APPROVED: "bg-emerald-100 dark:bg-emerald-500/20",
  REJECTED: "bg-red-100 dark:bg-red-500/20",
  PUBLISHED: "bg-emerald-100 dark:bg-emerald-500/20",
  RETIRED: "bg-slate-100 dark:bg-slate-500/20",
  VERSION_CREATED: "bg-indigo-100 dark:bg-indigo-500/20",
};

/* ── Console shell status dot/text tones ── */
export const STATUS_TONE = {
  running: {
    dot: "bg-indigo-500",
    text: "text-indigo-600 dark:text-indigo-400",
  },
  pass: {
    dot: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
  },
  warn: {
    dot: "bg-amber-500",
    text: "text-amber-600 dark:text-amber-400",
  },
  fail: {
    dot: "bg-red-500",
    text: "text-red-600 dark:text-red-400",
  },
} as const;

/* ── 2D orbit particle field tint per outcome ── */
export const TONE_PARTICLES: Record<Exclude<ConsoleTone, "neutral">, string[]> = {
  fail: ["#ef4444", "#f87171", "#fca5a5", "#dc2626"],
  pass: ["#10b981", "#34d399", "#6ee7b7", "#059669"],
};

/* ── 2D orbit geometry ── */
export const ORBIT_BOX = 340;
export const ORBIT_RADIUS = 132;
export const PARTICLE_COUNT = 120;
export const PARTICLE_CLEARANCE = ORBIT_RADIUS + 38;

/* ── 3D scene geometry (see pipelineScene3dShaders for GLSL) ── */
export const RING_RADIUS = 3.15;
export const RING_TILT = -0.16;

/* ── Shared layer intelligence drawer tokens ─────────────────────────
 * Consumed by Consistency, Governance, Knowledge Quality, Relationship
 * and Domain drawers. Kept here so any tone tweak is one edit.          */

export interface FindingSeverityTone {
  bg: string;
  text: string;
  label: string;
}

export type FindingSeverity = "HIGH" | "MEDIUM" | "LOW" | "INFO";

export const FINDING_SEVERITY_TONE: Record<FindingSeverity, FindingSeverityTone> = {
  HIGH: {
    bg: "bg-red-500/15 border-red-500/25",
    text: "text-red-400",
    label: "High",
  },
  MEDIUM: {
    bg: "bg-amber-500/15 border-amber-500/25",
    text: "text-amber-400",
    label: "Medium",
  },
  LOW: {
    bg: "bg-sky-500/15 border-sky-500/25",
    text: "text-sky-400",
    label: "Low",
  },
  INFO: {
    bg: "bg-slate-500/15 border-slate-500/25",
    text: "text-slate-400",
    label: "Info",
  },
};

export const FINDING_SEVERITY_RANK: Record<FindingSeverity, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
  INFO: 3,
};

export const NEUTRAL_TONE: FindingSeverityTone = {
  bg: "bg-slate-500/15 border-slate-500/25",
  text: "text-slate-400",
  label: EMPTY_PLACEHOLDER,
};

export const RESOLVED_TONE: FindingSeverityTone = {
  bg: "bg-emerald-500/15 border-emerald-500/25",
  text: "text-emerald-400",
  label: "Resolved",
};

export const UNRESOLVED_TONE: FindingSeverityTone = {
  bg: "bg-red-500/15 border-red-500/25",
  text: "text-red-400",
  label: "Unresolved",
};

export const AMBIGUOUS_TONE: FindingSeverityTone = {
  bg: "bg-amber-500/15 border-amber-500/25",
  text: "text-amber-400",
  label: "Ambiguous",
};

/* Reference-gap status tone map (Consistency layer). */
export const REFERENCE_STATUS_TONE: Record<
  "RESOLVED" | "UNRESOLVED" | "AMBIGUOUS",
  FindingSeverityTone
> = {
  RESOLVED: RESOLVED_TONE,
  UNRESOLVED: UNRESOLVED_TONE,
  AMBIGUOUS: AMBIGUOUS_TONE,
};

/* Governance control strength tone map. */
export type GovernanceStrength = "STRONG" | "ADEQUATE" | "WEAK" | "MISSING";
export const GOVERNANCE_STRENGTH_TONE: Record<GovernanceStrength, FindingSeverityTone> = {
  STRONG: {
    bg: "bg-emerald-500/15 border-emerald-500/25",
    text: "text-emerald-400",
    label: "Strong",
  },
  ADEQUATE: {
    bg: "bg-indigo-500/15 border-indigo-500/25",
    text: "text-indigo-400",
    label: "Adequate",
  },
  WEAK: {
    bg: "bg-amber-500/15 border-amber-500/25",
    text: "text-amber-400",
    label: "Weak",
  },
  MISSING: {
    bg: "bg-red-500/15 border-red-500/25",
    text: "text-red-400",
    label: "Missing",
  },
};

/* Governance review-cycle status tone map. */
export type GovernanceCycleStatus = "DEFINED" | "PARTIALLY_DEFINED" | "MISSING";
export const GOVERNANCE_CYCLE_STATUS_TONE: Record<GovernanceCycleStatus, FindingSeverityTone> = {
  DEFINED: {
    bg: "bg-emerald-500/15 border-emerald-500/25",
    text: "text-emerald-400",
    label: "Defined",
  },
  PARTIALLY_DEFINED: {
    bg: "bg-amber-500/15 border-amber-500/25",
    text: "text-amber-400",
    label: "Partial",
  },
  MISSING: {
    bg: "bg-red-500/15 border-red-500/25",
    text: "text-red-400",
    label: "Missing",
  },
};

/* Governance ownership role → icon text color. */
export type GovernanceRoleType =
  | "OWNER"
  | "APPROVER"
  | "REVIEWER"
  | "CUSTODIAN"
  | "STAKEHOLDER";
export const GOVERNANCE_ROLE_ICON_CLS: Record<GovernanceRoleType, string> = {
  OWNER: "text-emerald-400",
  APPROVER: "text-indigo-400",
  REVIEWER: "text-cyan-400",
  CUSTODIAN: "text-amber-400",
  STAKEHOLDER: "text-slate-400",
};

/* Knowledge-quality evidence-strength text tone. */
export type KnowledgeEvidenceStrength = "HIGH" | "MEDIUM" | "LOW" | "NONE";
export const KNOWLEDGE_STRENGTH_TEXT_TONE: Record<KnowledgeEvidenceStrength, string> = {
  HIGH: "text-emerald-400",
  MEDIUM: "text-amber-400",
  LOW: "text-orange-400",
  NONE: "text-red-400",
};

/* Progress-bar tones used in Knowledge Quality entity table. */
export type ProgressBarTone = "emerald" | "indigo" | "cyan";
export const PROGRESS_BAR_TONE: Record<
  ProgressBarTone,
  { bar: string; text: string }
> = {
  emerald: { bar: "bg-emerald-500", text: "text-emerald-400" },
  indigo: { bar: "bg-indigo-500", text: "text-indigo-400" },
  cyan: { bar: "bg-cyan-500", text: "text-cyan-400" },
};

/* Relationship type tone map (open — unknown types fall back to NEUTRAL_TONE). */
export const RELATIONSHIP_TYPE_TONE: Record<string, FindingSeverityTone> = {
  BELONGS_TO: {
    bg: "bg-emerald-500/15 border-emerald-500/25",
    text: "text-emerald-400",
    label: "Belongs to",
  },
  REFERENCES: {
    bg: "bg-indigo-500/15 border-indigo-500/25",
    text: "text-indigo-400",
    label: "References",
  },
  CONTRADICTS: {
    bg: "bg-red-500/15 border-red-500/25",
    text: "text-red-400",
    label: "Contradicts",
  },
  DEPENDS_ON: {
    bg: "bg-amber-500/15 border-amber-500/25",
    text: "text-amber-400",
    label: "Depends on",
  },
  IMPLEMENTS: {
    bg: "bg-sky-500/15 border-sky-500/25",
    text: "text-sky-400",
    label: "Implements",
  },
  PARTIALLY_IMPLEMENTS: {
    bg: "bg-purple-500/15 border-purple-500/25",
    text: "text-purple-400",
    label: "Partially implements",
  },
  REFINES: {
    bg: "bg-violet-500/15 border-violet-500/25",
    text: "text-violet-400",
    label: "Refines",
  },
  SUPPORTS: {
    bg: "bg-cyan-500/15 border-cyan-500/25",
    text: "text-cyan-400",
    label: "Supports",
  },
  DUPLICATES: {
    bg: "bg-pink-500/15 border-pink-500/25",
    text: "text-pink-400",
    label: "Duplicates",
  },
};

/* Header icon-box tone per layer drawer. */
export interface LayerIconBoxTone {
  box: string;
  icon: string;
}
export const LAYER_ICON_BOX_TONE = {
  domain: {
    box: "bg-indigo-500/15 border border-indigo-500/25",
    icon: "text-indigo-400",
  },
  consistency: {
    box: "bg-fuchsia-500/15 border border-fuchsia-500/25",
    icon: "text-fuchsia-400",
  },
  governance: {
    box: "bg-teal-500/15 border border-teal-500/25",
    icon: "text-teal-400",
  },
  knowledgeQuality: {
    box: "bg-yellow-500/15 border border-yellow-500/25",
    icon: "text-yellow-400",
  },
  relationship: {
    box: "bg-emerald-500/15 border border-emerald-500/25",
    icon: "text-emerald-400",
  },
} as const satisfies Record<string, LayerIconBoxTone>;

/* Fallback badge class when a document-type key is not in DOC_TYPE_COLORS. */
export const DOC_TYPE_FALLBACK_BADGE_CLS =
  "bg-[#f1f5f9] dark:bg-white/[0.05] text-[#475569] dark:text-white/60";

/* Score ring geometry — shared by Consistency, Governance, Knowledge Quality. */
export const SCORE_RING_SIZE = CONFIDENCE_RING_SIZE;
export const SCORE_RING_STROKE = CONFIDENCE_RING_STROKE;

export const DEFAULT_PAGE_SIZE = 12;

/* ── Percentage tone tiers (shared: registry overview + domain header) ──
 * Named so a future API can retune them in one place; the pctTier()
 * lookup lives in utils.ts with the other formatting helpers.            */
export type Tier = "good" | "ok" | "risk";

export const PCT_GOOD_MIN = 70;
export const PCT_OK_MIN = 50;

export const TIER_CLS: Record<Tier, { text: string; bar: string; stroke: string }> = {
  good: {
    text: "text-emerald-600 dark:text-emerald-400",
    bar: "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.45)] dark:shadow-[0_0_10px_rgba(52,211,153,0.65)]",
    stroke: "stroke-emerald-500 dark:stroke-emerald-400",
  },
  ok: {
    text: "text-amber-600 dark:text-amber-400",
    bar: "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)] dark:shadow-[0_0_10px_rgba(251,191,36,0.6)]",
    stroke: "stroke-amber-500 dark:stroke-amber-400",
  },
  risk: {
    text: "text-red-600 dark:text-red-400",
    bar: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)] dark:shadow-[0_0_10px_rgba(248,113,113,0.6)]",
    stroke: "stroke-red-500 dark:stroke-red-400",
  },
};
